import React from 'react';
import { Box, TextField, Select, MenuItem } from '@mui/material';

interface Country {
  code: string;
  dial: string;
  label: string; // short display label
  name: string;
}

const COUNTRIES: Country[] = [
  { code: 'US', dial: '+1', label: 'US +1', name: 'United States (+1)' },
  { code: 'IN', dial: '+91', label: 'IN +91', name: 'India (+91)' },
  { code: 'GB', dial: '+44', label: 'GB +44', name: 'United Kingdom (+44)' },
  { code: 'CA', dial: '+1', label: 'CA +1', name: 'Canada (+1)' },
  { code: 'AU', dial: '+61', label: 'AU +61', name: 'Australia (+61)' },
  { code: 'DE', dial: '+49', label: 'DE +49', name: 'Germany (+49)' },
  { code: 'FR', dial: '+33', label: 'FR +33', name: 'France (+33)' },
  { code: 'JP', dial: '+81', label: 'JP +81', name: 'Japan (+81)' },
  { code: 'SG', dial: '+65', label: 'SG +65', name: 'Singapore (+65)' },
  { code: 'AE', dial: '+971', label: 'AE +971', name: 'UAE (+971)' },
  { code: 'BR', dial: '+55', label: 'BR +55', name: 'Brazil (+55)' },
  { code: 'MX', dial: '+52', label: 'MX +52', name: 'Mexico (+52)' },
  { code: 'ZA', dial: '+27', label: 'ZA +27', name: 'South Africa (+27)' },
  { code: 'KR', dial: '+82', label: 'KR +82', name: 'South Korea (+82)' },
  { code: 'CN', dial: '+86', label: 'CN +86', name: 'China (+86)' },
  { code: 'NZ', dial: '+64', label: 'NZ +64', name: 'New Zealand (+64)' },
  { code: 'PH', dial: '+63', label: 'PH +63', name: 'Philippines (+63)' },
  { code: 'MY', dial: '+60', label: 'MY +60', name: 'Malaysia (+60)' },
  { code: 'IL', dial: '+972', label: 'IL +972', name: 'Israel (+972)' },
  { code: 'SA', dial: '+966', label: 'SA +966', name: 'Saudi Arabia (+966)' },
];

interface PhoneInputProps {
  value: string; // combined string like "+91 9876543210"
  onChange: (value: string) => void;
  label?: string;
  size?: 'small' | 'medium';
}

/**
 * Parse a combined phone string into country code and number.
 * Handles formats like "+91 9876543210", "+1 555-1234", or just "9876543210"
 */
function parsePhone(value: string): { countryCode: string; number: string } {
  if (!value) return { countryCode: 'US', number: '' };

  const trimmed = value.trim();

  if (trimmed.startsWith('+')) {
    // Try matching longest dial code first
    for (const country of [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)) {
      if (trimmed.startsWith(country.dial)) {
        return {
          countryCode: country.code,
          number: trimmed.slice(country.dial.length).trim(),
        };
      }
    }
  }

  // No match — return as-is with default country
  return { countryCode: 'US', number: trimmed };
}

export default function PhoneInput({ value, onChange, label = 'Phone Number', size = 'small' }: PhoneInputProps) {
  const parsed = parsePhone(value);

  const handleCountryChange = (newCode: string) => {
    const country = COUNTRIES.find(c => c.code === newCode);
    if (!country) return;
    onChange(parsed.number ? `${country.dial} ${parsed.number}` : country.dial);
  };

  const handleNumberChange = (newNumber: string) => {
    const country = COUNTRIES.find(c => c.code === parsed.countryCode);
    const dial = country?.dial || '+1';
    onChange(newNumber.trim() ? `${dial} ${newNumber.trim()}` : '');
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, flex: 1, minWidth: 0 }}>
      <Select
        value={parsed.countryCode}
        onChange={(e) => handleCountryChange(e.target.value)}
        size={size}
        sx={{ minWidth: 100, flexShrink: 0, fontSize: 13 }}
        renderValue={(code) => {
          const c = COUNTRIES.find(cc => cc.code === code);
          return c ? c.label : code;
        }}
      >
        {COUNTRIES.map(c => (
          <MenuItem key={c.code} value={c.code} sx={{ fontSize: 13 }}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
      <TextField
        label={label}
        value={parsed.number}
        onChange={(e) => handleNumberChange(e.target.value)}
        fullWidth
        size={size}
      />
    </Box>
  );
}

export { COUNTRIES, parsePhone };
